import { useEffect, useState, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Search, ShoppingCart, Heart, MessageSquare, PackageCheck, SkipForward } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Onboarding tour para clientes nuevos.
 *
 * Se muestra automáticamente la primera vez que el cliente entra al catálogo
 * (marcado en localStorage con la key `cm_tour_completed`).
 * Se puede reabrir con el botón "?" del header.
 *
 * Diseño responsive:
 *  - Desktop (>= 640px): tooltip flotante junto al elemento resaltado.
 *  - Móvil (< 640px): tooltip fijo abajo del viewport (bottom dock) — el
 *    resaltado queda alrededor del target y el usuario puede leer sin
 *    depender de cálculos de posición finos.
 *  - `position: fixed` + getBoundingClientRect() → siempre alineado aunque
 *    el usuario haga scroll.
 *  - Se recomputa la posición al scroll/resize/step-change con RAF.
 */

const STEPS = [
  {
    id: "catalog",
    icon: Search,
    title: "1. Busca en el catálogo",
    body: "Escribe el nombre o el código del producto en la barra de arriba, o filtra por marca y categoría. Los precios que ves acá son los tuyos: los de tu lista comercial.",
    target: "[data-testid='search-input']",
    placement: "bottom",
  },
  {
    id: "cart",
    icon: ShoppingCart,
    title: "2. Arma tu pedido",
    body: "Ajusta la cantidad con los botones + y − y dale en Agregar. Cada producto que sumes se guarda en tu carrito, arriba a la derecha, con el total actualizado.",
    target: "[data-testid^='add-to-cart-']",
    placement: "top",
  },
  {
    id: "favorites",
    icon: Heart,
    title: "3. Guarda tus favoritos",
    body: "Toca el corazoncito para marcar los productos que pides seguido. Después los encuentras en un solo clic desde el menú Favoritos, sin buscarlos de nuevo.",
    target: "[data-testid^='favorite-toggle-']",
    placement: "left",
  },
  {
    id: "notes",
    icon: MessageSquare,
    title: "4. Deja tus observaciones",
    body: "Cuando entres al carrito vas a ver un campo de Observaciones. Ahí puedes escribir referencias de entrega, notas para el conductor o cualquier instrucción para tu asesor.",
    target: "[data-testid='open-cart-btn']",
    placement: "bottom",
  },
  {
    id: "checkout",
    icon: PackageCheck,
    title: "5. Confirma y listo",
    body: "En el carrito revisas el total, escoges pago de contado o a crédito (según tu cupo) y confirmas. Tu pedido entra directo a Arcos y en minutos lo estamos alistando.",
    target: "[data-testid='open-cart-btn']",
    placement: "bottom",
  },
];

const TOUR_KEY = "cm_tour_completed";
const CARD_W = 340;
const GAP = 12;
const MOBILE_BREAK = 640;

export const OnboardingTour = ({ open, onClose }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);   // viewport-relative rect of target
  const [isMobile, setIsMobile] = useState(false);
  const [tooltipHeight, setTooltipHeight] = useState(220);
  const tooltipRef = useRef(null);
  const rafRef = useRef(0);

  const step = STEPS[stepIdx];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    // Si el target quedó fuera del viewport, aún se muestra rect (para el
    // highlight fantasma) pero el tooltip caerá al centro/dock.
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // Scroll target into view when the step changes, then measure.
  useEffect(() => {
    if (!open || !step) return;
    setRect(null);
    const el = document.querySelector(step.target);
    if (el) {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } catch { el.scrollIntoView(); }
    }
    // Wait for smooth scroll to finish, then measure. If browser doesn't
    // finish in 400ms we still take our best measurement.
    const t1 = setTimeout(measure, 350);
    const t2 = setTimeout(measure, 700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [open, step, measure]);

  // Continuous re-measurement on scroll/resize so the highlight stays glued
  // to the target as the page moves.
  useEffect(() => {
    if (!open) return;
    const onMove = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    const onResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAK);
      onMove();
    };
    setIsMobile(window.innerWidth < MOBILE_BREAK);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onResize);
    document.body.style.overflow = "hidden"; // block background scroll while overlay is up
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onResize);
      document.body.style.overflow = "";
    };
  }, [open, measure]);

  // Track real tooltip height for accurate placement (some steps have longer text)
  useEffect(() => {
    if (!open) return;
    const el = tooltipRef.current;
    if (el) setTooltipHeight(el.offsetHeight);
  }, [open, stepIdx, rect]);

  const finish = useCallback(() => {
    localStorage.setItem(TOUR_KEY, "1");
    // Persist server-side so it doesn't reappear on other devices/sessions.
    // Fire-and-forget: no need to await this.
    try {
      import("../lib/api").then(({ clientApi }) => {
        clientApi.post("/portal/client-state/onboarding-completed").catch(() => {});
      });
    } catch { /* ignore */ }
    setStepIdx(0);
    onClose();
  }, [onClose]);

  const next = () => (stepIdx < STEPS.length - 1 ? setStepIdx(stepIdx + 1) : finish());
  const prev = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  // Close on ESC for keyboard accessibility
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") finish(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, finish]);

  if (!open || !step) return null;
  const Icon = step.icon;

  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;

  // Compute tooltip position (viewport coords, position: fixed)
  let tooltipStyle;
  if (isMobile) {
    // Móvil: siempre en la parte inferior fija, ocupando el ancho útil.
    tooltipStyle = {
      position: "fixed",
      left: 12, right: 12,
      bottom: 12,
      width: "auto",
    };
  } else if (!rect) {
    // Fallback: centrado (target no encontrado o fuera)
    tooltipStyle = {
      position: "fixed",
      top: Math.max(16, vh / 2 - tooltipHeight / 2),
      left: Math.max(16, vw / 2 - CARD_W / 2),
      width: CARD_W,
    };
  } else {
    const centerX = rect.left + rect.width / 2 - CARD_W / 2;
    const safeLeft = Math.max(16, Math.min(vw - CARD_W - 16, centerX));
    let top;
    if (step.placement === "top" && rect.top - tooltipHeight - GAP > 16) {
      top = rect.top - tooltipHeight - GAP;
    } else if (step.placement === "left" && rect.left - CARD_W - GAP > 16) {
      // side-left variant, keep centerY
      top = Math.max(16, Math.min(vh - tooltipHeight - 16, rect.top + rect.height / 2 - tooltipHeight / 2));
      tooltipStyle = { position: "fixed", top, left: rect.left - CARD_W - GAP, width: CARD_W };
    } else if (rect.bottom + tooltipHeight + GAP < vh - 16) {
      // default bottom
      top = rect.bottom + GAP;
    } else if (rect.top - tooltipHeight - GAP > 16) {
      // fallback to top if bottom won't fit
      top = rect.top - tooltipHeight - GAP;
    } else {
      // last resort: dock to bottom of viewport
      top = vh - tooltipHeight - 16;
    }
    if (!tooltipStyle) {
      tooltipStyle = { position: "fixed", top, left: safeLeft, width: CARD_W };
    }
  }

  // Highlight rectangle (fixed position, viewport coords)
  const highlightStyle = rect
    ? {
        position: "fixed",
        top: rect.top - 6,
        left: rect.left - 6,
        width: rect.width + 12,
        height: rect.height + 12,
      }
    : null;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="onboarding-tour">
      {/* Backdrop with cutout — using two overlays so the highlight window
          is visible but the rest of the screen is dimmed and non-interactive. */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={finish} />
      {highlightStyle && (
        <div
          className="rounded-lg ring-4 ring-cm-yellow pointer-events-none transition-all duration-300"
          style={{
            ...highlightStyle,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="bg-white rounded-lg shadow-2xl border border-cm-border overflow-hidden max-w-[calc(100vw-24px)]"
        style={tooltipStyle}
        data-testid="tour-tooltip"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-cm-ink text-cm-cream px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-4 w-4 text-cm-yellow flex-shrink-0" />
            <span className="text-[10px] sm:text-xs uppercase tracking-widest font-medium truncate">
              Tutorial · Paso {stepIdx + 1} de {STEPS.length}
            </span>
          </div>
          <button
            onClick={finish}
            className="text-cm-cream/70 hover:text-cm-cream transition-colors flex-shrink-0"
            data-testid="tour-close-btn"
            aria-label="Cerrar tutorial"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-serif text-lg mb-2">{step.title}</h3>
          <p className="text-sm text-cm-ink-soft leading-relaxed">{step.body}</p>
          <div className="flex gap-1.5 mt-4 mb-1">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${i === stepIdx ? "bg-cm-red" : i < stepIdx ? "bg-cm-red/40" : "bg-cm-cream-alt"}`}
              />
            ))}
          </div>
        </div>
        <div className="px-4 py-3 bg-cm-cream-alt/40 flex items-center justify-between gap-2 flex-wrap">
          <Button
            variant="ghost" size="sm"
            onClick={prev}
            disabled={stepIdx === 0}
            data-testid="tour-prev-btn"
            className="text-cm-ink-soft"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
          </Button>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="sm"
              onClick={finish}
              className="text-xs text-cm-ink-soft hover:text-cm-ink"
              data-testid="tour-skip-btn"
              title="Cerrar el tutorial"
            >
              <SkipForward className="h-3.5 w-3.5 mr-1" /> Saltar
            </Button>
            <Button
              size="sm"
              onClick={next}
              className="bg-cm-red text-white hover:bg-cm-red/90"
              data-testid="tour-next-btn"
            >
              {stepIdx === STEPS.length - 1 ? "¡Listo!" : "Siguiente"}
              {stepIdx < STEPS.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const shouldShowTourAuto = () => {
  return !localStorage.getItem(TOUR_KEY);
};
